/**
 * Showroom carousel for the hero banner — slides through the dealership's
 * real top vehicles (studio render + model + live figures). Auto-advances,
 * pauses on hover/focus, and honours reduced-motion.
 *
 * Layout: a full-width stage with the car on top, and a footer row below —
 * badge, name and figures on the left, arrows + dots on the right — so the
 * car and its caption never overlap.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './modern.css';

export default function HeroCarousel({ slides = [], interval = 5200 }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const active = n ? index % n : 0;

  useEffect(() => {
    if (n < 2 || paused) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % n), interval);
    return () => window.clearInterval(id);
  }, [n, paused, interval]);

  if (!n) return null;
  const go = (i) => setIndex(((i % n) + n) % n);

  return (
    <div
      className="md-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      <div className="md-carousel-stage">
        <div className="md-carousel-spot" />
        {slides.map((s, i) => {
          const state = i === active ? 'on' : (i === (active - 1 + n) % n ? 'prev' : 'next');
          return (
            <div key={s.key} className={`md-slide ${state}`} aria-hidden={i !== active}>
              {s.photo && (
                <img src={s.photo} alt={s.title} loading={i === 0 ? 'eager' : 'lazy'}
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
              )}
            </div>
          );
        })}
        <div className="md-slide-floor" />
      </div>

      <div className="md-carousel-foot">
        <div className="md-carousel-info">
          {slides.map((s, i) => (
            <div key={s.key} className={`md-slide-info${i === active ? ' on' : ''}`} aria-hidden={i !== active}>
              {s.badge && <span className="md-slide-badge">{s.badge}</span>}
              <div className="md-slide-title">{s.to ? <Link to={s.to} tabIndex={i === active ? 0 : -1}>{s.title}</Link> : s.title}</div>
              {s.facts?.length > 0 && (
                <div className="md-slide-facts">
                  {s.facts.map((f) => <span key={f}>{f}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>

        {n > 1 && (
          <div className="md-carousel-nav">
            <button type="button" className="md-carousel-arrow" onClick={() => go(active - 1)} aria-label="Previous vehicle">‹</button>
            <div className="md-carousel-dots" role="tablist">
              {slides.map((s, i) => (
                <button key={s.key} type="button" role="tab" aria-selected={i === active}
                  aria-label={`Show ${s.title}`} className={i === active ? 'on' : ''} onClick={() => go(i)} />
              ))}
            </div>
            <button type="button" className="md-carousel-arrow" onClick={() => go(active + 1)} aria-label="Next vehicle">›</button>
          </div>
        )}
      </div>
    </div>
  );
}
